import { Injectable } from '@angular/core';

import { FormlyFieldConfig } from '@ngx-formly/core';

import { FormModelParser } from '../helpers/form-model-parser';

@Injectable({
  providedIn: 'root'
})
export class ResponseParserService {

  // These methods return the model of a single item (or null/undefined value if not found).
  // If pageIndex/sectionIndex is null/undefined, then it is assumed that the page/section is non-repeating.

  findNavTabModel(model: any, navTabNumber: number): any {
    return FormModelParser.getNavTabModel(model, [navTabNumber]);
  }

  findPageModel(model: any, navTabNumber: number, pageNumber: number, pageIndex: number): any {
    return FormModelParser.getPageModel(model, [navTabNumber, pageNumber], pageIndex);
  }

  findSectionModel(model: any, navTabNumber: number, pageNumber: number, pageIndex: number, sectionCode: string, sectionIndex: number): any {
    return FormModelParser.getSectionModel(model, [navTabNumber, pageNumber, sectionCode], pageIndex, sectionIndex);
  }

  findQuestionModel(model: any, navTabNumber: number, pageNumber: number, pageIndex: number, sectionCode: string, sectionIndex: number, questionCode: string): any {
    return FormModelParser.getQuestionModel(model, [navTabNumber, pageNumber, sectionCode, questionCode], pageIndex, sectionIndex);
  }

  // These methods return the models of all the items (or an empty array if not found).
  // If the page/section is non-repeating, then only up to one item is returned.
  // If the page/section is repeating, then all the items are returned.

  findNavTabItems(model: any, navTabNumber: number): any[] {
    return FormModelParser.getNavTabItems(model, { navTabPath: [navTabNumber] });
  }

  findPageItems(model: any, navTabNumber: number, pageNumber: number, isRepeatingPage: boolean): any[] {
    return FormModelParser.getPageItems(model, { pagePath: [navTabNumber, pageNumber], isRepeatingPage });
  }

  findSectionItems(model: any, navTabNumber: number, pageNumber: number, isRepeatingPage: boolean, sectionCode: string, isRepeatingSection: boolean): any[] {
    return FormModelParser.getSectionItems(model, { sectionPath: [navTabNumber, pageNumber, sectionCode], isRepeatingPage, isRepeatingSection });
  }

  findQuestionItems(model: any, navTabNumber: number, pageNumber: number, isRepeatingPage: boolean, sectionCode: string, isRepeatingSection: boolean, questionCode: string): any[] {
    return FormModelParser.getQuestionItems(model, { questionPath: [navTabNumber, pageNumber, sectionCode, questionCode], isRepeatingPage, isRepeatingSection });
  }

  // These methods set the model of a single item.
  // If pageIndex/sectionIndex is null/undefined, then it is assumed that the page/section is non-repeating.
  // If the parent node does not exist, the item is not set.

  replaceNavTabModel(model: any, navTabNumber: number, newNavTabModel: any): boolean {
    return FormModelParser.setNavTabModel(model, [navTabNumber], newNavTabModel);
  }

  replacePageModel(model: any, navTabNumber: number, pageNumber: number, pageIndex: number, newPageModel: any): boolean {
    return FormModelParser.setPageModel(model, [navTabNumber, pageNumber], pageIndex, newPageModel);
  }

  replaceSectionModel(model: any, navTabNumber: number, pageNumber: number, pageIndex: number, sectionCode: string, sectionIndex: number, newSectionModel: any): boolean {
    return FormModelParser.setSectionModel(model, [navTabNumber, pageNumber, sectionCode], pageIndex, sectionIndex, newSectionModel);
  }

  replaceQuestionModel(model: any, navTabNumber: number, pageNumber: number, pageIndex: number, sectionCode: string, sectionIndex: number, questionCode: string, newQuestionModel: any): boolean {
    return FormModelParser.setQuestionModel(model, [navTabNumber, pageNumber, sectionCode, questionCode], pageIndex, sectionIndex, newQuestionModel);
  }

  // These methods set the model of a single item.
  // If pageIndex/sectionIndex is null/undefined, then it is assumed that the page/section is non-repeating.
  // If any of the parent nodes is nullish, it is created, so that the item is always guaranteed to be set.

  ensureFormModel(model: any): any {
    return FormModelParser.ensureFormModel(model);
  }

  ensureNavTabModel(model: any, navTabNumber: number, newNavTabModel: any): any {
    return FormModelParser.ensureNavTabModel(model, [navTabNumber], newNavTabModel);
  }

  ensurePageModel(model: any, navTabNumber: number, pageNumber: number, pageIndex: number, newPageModel: any): any {
    return FormModelParser.ensurePageModel(model, [navTabNumber, pageNumber], pageIndex, newPageModel);
  }

  ensureSectionModel(model: any, navTabNumber: number, pageNumber: number, pageIndex: number, sectionCode: string, sectionIndex: number, newSectionModel: any): any {
    return FormModelParser.ensureSectionModel(model, [navTabNumber, pageNumber, sectionCode], pageIndex, sectionIndex, newSectionModel);
  }

  ensureQuestionModel(model: any, navTabNumber: number, pageNumber: number, pageIndex: number, sectionCode: string, sectionIndex: number, questionCode: string, newQuestionModel: any): any {
    return FormModelParser.ensureQuestionModel(model, [navTabNumber, pageNumber, sectionCode, questionCode], pageIndex, sectionIndex, newQuestionModel);
  }

  // Other miscellaneous methods

  modelHasQuestions(model: any, formConfig: FormlyFieldConfig[]): boolean {
    return FormModelParser.modelHasQuestions(model, formConfig);
  }
}
